import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMatrix } from '../api';

export default function MatrixPage() {
  const [data, setData] = useState([]);
  const [sortKey, setSortKey] = useState('note_sectorielle');
  const [sortDir, setSortDir] = useState('desc');
  const [filter, setFilter] = useState('');
  const [secteurFilter, setSecteurFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getMatrix()
      .then(res => {
        const list = Array.isArray(res) ? res : (res && res.startups) || [];
        const flat = list.map(s => ({
          ...s,
          categorie: s.categorie ?? s.secteur,
          ...(s.scores || {}),
        }));
        setData(flat);
      })
      .catch(console.error);
  }, []);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  // Etat du score affiche a cote de la date : le calcul n'est declenche qu'a la
  // main depuis la fiche dossier, une ligne peut donc etre non calculee ou
  // anterieure aux dernieres reponses du candidat.
  const scoreState = (s) => {
    if (!s.has_score) {
      return <span style={{ color: '#B26A00', background: '#FFF3E0', border: '1px solid #FFCC80',
        borderRadius: 10, padding: '2px 7px' }}>non calculé</span>;
    }
    const d = s.calculated_at ? new Date(s.calculated_at).toLocaleDateString('fr-FR') : '-';
    if (s.score_stale) {
      return <span title="Le QIT a été modifié après ce calcul : relancez le scoring depuis la fiche dossier."
        style={{ color: '#C62828', background: '#FFEBEE', border: '1px solid #FFCDD2',
          borderRadius: 10, padding: '2px 7px' }}>{d} · à recalculer</span>;
    }
    return <span style={{ color: 'var(--text-light)' }}>{d}</span>;
  };

  const scoreColor = (v) => {
    const n = parseFloat(v);
    if (!n && n !== 0) return {};
    const color = n > 7 ? '#27AE60' : n >= 4 ? '#F39C12' : '#E74C3C';
    return { color, fontWeight: 600, background: `${color}10` };
  };

  const filtered = data
    .filter(s => !filter || s.name.toLowerCase().includes(filter.toLowerCase()))
    .filter(s => !secteurFilter || s.categorie === secteurFilter)
    .sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const secteurs = [...new Set(data.map(s => s.categorie).filter(Boolean))].sort();

  // Compteurs sur la liste affichee, pour rester coherents avec les filtres.
  const missing = filtered.filter(s => !s.has_score).length;
  const stale = filtered.filter(s => s.score_stale).length;

  const cols = [
    { key: 'name', label: 'Startup', w: 140 },
    { key: 'categorie', label: 'Catégorie', w: 90 },
    { key: 'sous_domaine', label: 'Sous-domaine', w: 130 },
    { key: 'qit_progress', label: 'QIT', w: 55 },
    { key: 'calculated_at', label: 'Score calculé le', w: 110 },
    { key: 'cl01_bp', label: 'BP' }, { key: 'cl02_ratio', label: 'Ratio' },
    { key: 'cl03_solidite_fi', label: 'Sol.Fi' }, { key: 'cl04_maturite_fi', label: 'Mat.Fi' },
    { key: 'cl05_marche', label: 'Marché' }, { key: 'cl06_innovation', label: 'Innov.' },
    { key: 'cl07_equipe', label: 'Équipe' }, { key: 'cl08_org_comm', label: 'Org.C' },
    { key: 'cl09_notoriete', label: 'Notor.' }, { key: 'cl10_rse', label: 'RSE' },
    { key: 'cl11_rgpd', label: 'RGPD' }, { key: 'cl12_conformite', label: 'Conf.' },
    { key: 'note_pivot', label: 'Pivot', w: 60 },
    { key: 'note_sectorielle', label: 'ISI', w: 60 },
    { key: 'risk_level', label: 'Risque', w: 80 },
  ];

  return (
    <div style={{ padding: '32px 0' }}>
      <div className="container">
        <div className="section-header">
          <h1>Matrice de Classement</h1>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Filtrer par nom..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{ padding: '10px 16px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, minWidth: 250 }}
          />
          <select value={secteurFilter} onChange={(e) => setSecteurFilter(e.target.value)}
            style={{ padding: '10px 16px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14 }}>
            <option value="">Tous les secteurs</option>
            {secteurs.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ padding: '10px 0', color: 'var(--text-light)', fontSize: 14 }}>
            {filtered.length} startup{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {(missing > 0 || stale > 0) && (
          <div style={{ padding: '10px 14px', marginBottom: 20, borderRadius: 8, fontSize: 12.5,
            background: '#FFF8E1', border: '1px solid #FFE082', color: '#8D6E20' }}>
            Le score ISI n'est pas recalculé automatiquement : il date du dernier déclenchement
            manuel depuis la fiche dossier.
            {missing > 0 && <> <strong>{missing}</strong> dossier(s) avec un QIT renseigné n'ont
              jamais été scorés.</>}
            {stale > 0 && <> <strong>{stale}</strong> score(s) sont antérieurs aux dernières
              réponses du candidat.</>}
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table className="matrix-table">
            <thead>
              <tr>
                {cols.map(c => (
                  <th key={c.key} onClick={() => handleSort(c.key)}
                    style={{ minWidth: c.w || 55, fontSize: 11 }}>
                    {c.label} {sortKey === c.key ? (sortDir === 'asc' ? '\u25b2' : '\u25bc') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} onClick={() => navigate(`/startup/${s.id}`)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{s.name}</td>
                  <td><span className="badge badge-secteur" style={{ fontSize: 10 }}>{s.categorie}</span></td>
                  <td style={{ fontSize: 12 }}>{s.sous_domaine}</td>
                  <td style={{ textAlign: 'center', fontSize: 12 }}>
                    {s.qit_progress != null ? `${s.qit_progress}%` : '-'}
                  </td>
                  <td style={{ textAlign: 'center', fontSize: 11, whiteSpace: 'nowrap' }}>
                    {scoreState(s)}
                  </td>
                  {['cl01_bp','cl02_ratio','cl03_solidite_fi','cl04_maturite_fi','cl05_marche',
                    'cl06_innovation','cl07_equipe','cl08_org_comm','cl09_notoriete','cl10_rse',
                    'cl11_rgpd','cl12_conformite'].map(k => (
                    <td key={k} style={{ textAlign: 'center', ...scoreColor(s[k]), fontSize: 12 }}>
                      {s[k] != null ? parseFloat(s[k]).toFixed(1) : '-'}
                    </td>
                  ))}
                  <td style={{ textAlign: 'center', fontWeight: 700, fontSize: 12 }}>
                    {s.note_pivot ? parseFloat(s.note_pivot).toFixed(1) : '-'}
                  </td>
                  <td style={{ textAlign: 'center', fontWeight: 700, fontSize: 13,
                    ...scoreColor(s.note_sectorielle) }}>
                    {s.note_sectorielle ? parseFloat(s.note_sectorielle).toFixed(1) : '-'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`badge badge-${s.risk_level}`} style={{ fontSize: 10 }}>
                      {s.risk_level === 'vert' ? 'Vert' : s.risk_level === 'orange' ? 'Orange' : 'Rouge'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
